/**
 * Production Report — mock dataset cho 5 tab phụ (Dashboard, Production Analysis,
 * Thông tin qui chuẩn, Comparison, BOM-BTA1) theo spec
 * `frontend/plan/Excel_Production_Report_Web_UI_Conversion_Spec.md`.
 *
 * Dữ liệu lấy từ workbook `3. PRO-TLBL-25097 - BTA1 (Đắk Lắk).xlsm` (PO
 * `PRO-TLBL-25097`). Khi BE expose `/api/production-report/{po_no}/...` thì
 * swap các const dưới đây bằng RTK Query data.
 */

/* ---------- shared context ----------------------------------------------- */

export const SAMPLE_PO_NO = 'PRO-TLBL-25097'
export const SAMPLE_PO_TYPE = 'BTA1'
export const SAMPLE_BOM_CODE = 'BTA1'
export const SAMPLE_COMPARISON_PO_NO = 'PRO-TLBL-25086'

/* ---------- Dashboard (§8) ------------------------------------------------ */

export interface DashboardKpis {
  inputNetWeight: number
  outputNetWeight: number
  outTurn: number
  loss: number
  totalValueIn: number
  totalValueOut: number
  profitLoss: number
}

export const DASHBOARD_KPIS: DashboardKpis = {
  inputNetWeight: 282.163,
  outputNetWeight: 269.412,
  outTurn: 0.9548,
  loss: 0.0452,
  totalValueIn: 1024132,
  totalValueOut: 1057896,
  profitLoss: 33764,
}

export interface CategoryDistribution {
  category: string
  mt: number
}

export const OUTPUT_CATEGORY_DISTRIBUTION: CategoryDistribution[] = [
  { category: 'Graded', mt: 198.7 },
  { category: 'By-products', mt: 52.4 },
  { category: 'Rejects', mt: 8.6 },
  { category: 'Husk/Waste', mt: 9.7 },
]

export interface ItemMt {
  item: string
  mt: number
}

export const OUTPUT_ITEMS_MT: ItemMt[] = [
  { item: 'R13-ST1', mt: 41.2 },
  { item: 'R13-ST8', mt: 38.8 },
  { item: 'R16-CL1', mt: 36.5 },
  { item: 'R18-CL1', mt: 28.1 },
  { item: 'R16-CSR', mt: 19.3 },
  { item: 'R18-CSR', mt: 14.7 },
  { item: 'R13-GTR', mt: 11.5 },
  { item: 'R-P16-ST', mt: 7.1 },
  { item: 'R-DF', mt: 4.6 },
  { item: 'R-DSR', mt: 0.1 },
]

export interface BomCompareRow {
  label: string
  actual: number
  bom: number
}

export const CATEGORY_ACTUAL_VS_BOM: BomCompareRow[] = [
  { label: 'Graded', actual: 73.8, bom: 70.0 },
  { label: 'By-products', actual: 19.4, bom: 22.0 },
  { label: 'Rejects', actual: 3.2, bom: 3.5 },
  { label: 'Husk/Waste', actual: 3.6, bom: 4.5 },
]

export const ITEM_ACTUAL_VS_BOM: BomCompareRow[] = [
  { label: 'R13-ST1', actual: 15.3, bom: 14.0 },
  { label: 'R13-ST8', actual: 14.4, bom: 13.5 },
  { label: 'R16-CL1', actual: 13.5, bom: 13.0 },
  { label: 'R18-CL1', actual: 10.4, bom: 11.0 },
  { label: 'R16-CSR', actual: 7.2, bom: 7.5 },
  { label: 'R18-CSR', actual: 5.5, bom: 6.0 },
  { label: 'R13-GTR', actual: 4.3, bom: 4.5 },
  { label: 'R-P16-ST', actual: 2.6, bom: 3.0 },
]

export interface QualityCompareRow {
  label: string
  input: number
  output: number
}

export const QUALITY_OUTPUT_VS_INPUT_LEFT: QualityCompareRow[] = [
  { label: 'MC', input: 13.04, output: 12.55 },
  { label: 'Blacks', input: 1.95, output: 0.42 },
  { label: 'Brokens', input: 2.71, output: 1.18 },
  { label: 'Black & Broken', input: 4.66, output: 1.6 },
  { label: 'FM', input: 1.34, output: 0.28 },
  { label: 'Mouldy', input: 0.5, output: 0.1 },
]

export const QUALITY_OUTPUT_VS_INPUT_RIGHT: QualityCompareRow[] = [
  { label: 'S20', input: 13.04, output: 9.8 },
  { label: 'S19', input: 23.37, output: 27.9 },
  { label: 'S18', input: 41.82, output: 48.6 },
  { label: 'S16', input: 79.31, output: 84.2 },
  { label: 'S13', input: 92.4, output: 95.1 },
  { label: '<S12', input: 7.6, output: 4.9 },
]

/* ---------- Production Analysis (§4) -------------------------------------- */

export interface ProductionAnalysisHeader {
  bomCode: string
  liffePrice: number
  g2Diffs: number
  poNumber: string
  inputCategorySummary: string
  outputCategorySummary: string
  mode: 'qui chuan' | 'chua qui chuan'
}

export const PRODUCTION_ANALYSIS_HEADER: ProductionAnalysisHeader = {
  bomCode: SAMPLE_BOM_CODE,
  liffePrice: 3450,
  g2Diffs: 180,
  poNumber: SAMPLE_PO_NO,
  inputCategorySummary: 'RFAQ 282.163 Mt',
  outputCategorySummary: '15 items / 269.412 Mt',
  mode: 'chua qui chuan',
}

export interface QualityDeductionRow {
  item: string
  fmOutput: number
  fmStandard: number
  weight: number
  pctDeduct: number
  weightDeduct: number
}

export const QUALITY_DEDUCTION_ROWS: QualityDeductionRow[] = [
  {
    item: 'R13-ST8',
    fmOutput: 0.45,
    fmStandard: 0.3,
    weight: 38.8,
    pctDeduct: 0.5,
    weightDeduct: 0.194,
  },
]

export interface BudgetActualRow {
  code: string
  category: string
  premium: number
  bomPct: number
  actualMt: number
  actualPct: number
  varPct: number
  valuePerMt: number
  totalValue: number
  mc?: number
  blacks?: number
  brokens?: number
  blackBroken?: number
  fm?: number
  mouldy?: number
  brown?: number
  partlyBlack?: number
  immature?: number
  brokenHalf?: number
  s20?: number
  s19?: number
  s18?: number
  s16?: number
  s13?: number
  polishDegree?: number
}

export const INPUT_BUDGET_ACTUAL_ROWS: BudgetActualRow[] = [
  {
    code: 'RFAQ',
    category: 'FAQ',
    premium: 0,
    bomPct: 1.0,
    actualMt: 282.163,
    actualPct: 1.0,
    varPct: 0,
    valuePerMt: 3617.65,
    totalValue: 1020901,
    mc: 13.04,
    blacks: 1.95,
    brokens: 2.71,
    blackBroken: 4.66,
    fm: 1.34,
    mouldy: 0.5,
    brown: 0.1,
    partlyBlack: 0.2,
    immature: 0.3,
    brokenHalf: 0.4,
    s20: 13.04,
    s19: 23.37,
    s18: 41.82,
    s16: 79.31,
    s13: 92.4,
    polishDegree: 0,
  },
]

export const OUTPUT_BUDGET_ACTUAL_ROWS: BudgetActualRow[] = [
  { code: 'R13-ST1', category: 'Graded', premium: 220, bomPct: 0.14, actualMt: 41.2, actualPct: 0.153, varPct: 0.013, valuePerMt: 3850, totalValue: 158620,
    mc: 12.5, blacks: 0.3, brokens: 1.1, blackBroken: 1.4, fm: 0.2, s18: 90.1, s16: 95.3, s13: 99.0 },
  { code: 'R13-ST8', category: 'Graded', premium: 180, bomPct: 0.135, actualMt: 38.8, actualPct: 0.144, varPct: 0.009, valuePerMt: 3810, totalValue: 147828,
    mc: 12.4, blacks: 0.4, brokens: 1.5, blackBroken: 1.9, fm: 0.45, s18: 88.4, s16: 94.2, s13: 99.0 },
  { code: 'R16-CL1', category: 'Graded', premium: 250, bomPct: 0.130, actualMt: 36.5, actualPct: 0.135, varPct: 0.005, valuePerMt: 3880, totalValue: 141620,
    mc: 12.6, blacks: 0.3, brokens: 1.0, blackBroken: 1.3, fm: 0.18, s18: 95.0, s16: 99.3, s13: 100 },
  { code: 'R18-CL1', category: 'Graded', premium: 320, bomPct: 0.110, actualMt: 28.1, actualPct: 0.104, varPct: -0.006, valuePerMt: 3950, totalValue: 110995,
    mc: 13.2, blacks: 0.2, brokens: 0.9, blackBroken: 1.1, fm: 0.16, s18: 98.5, s16: 100, s13: 100 },
  { code: 'R16-CSR', category: 'Graded', premium: 200, bomPct: 0.075, actualMt: 19.3, actualPct: 0.072, varPct: -0.003, valuePerMt: 3830, totalValue: 73919,
    mc: 12.7, blacks: 0.4, brokens: 1.2, blackBroken: 1.6, fm: 0.22, s18: 87.0, s16: 96.0, s13: 99.0 },
  { code: 'R18-CSR', category: 'Graded', premium: 260, bomPct: 0.060, actualMt: 14.7, actualPct: 0.055, varPct: -0.005, valuePerMt: 3890, totalValue: 57183,
    mc: 12.9, blacks: 0.3, brokens: 1.0, blackBroken: 1.3, fm: 0.18, s18: 96.0, s16: 100, s13: 100 },
  { code: 'R13-GTR', category: 'By-products', premium: -150, bomPct: 0.045, actualMt: 11.5, actualPct: 0.043, varPct: -0.002, valuePerMt: 3480, totalValue: 40020,
    mc: 12.3, blacks: 10.0, brokens: 38.0, blackBroken: 48.0, fm: 4.0, s18: 0, s16: 25, s13: 80 },
  { code: 'R-P16-ST', category: 'By-products', premium: -120, bomPct: 0.030, actualMt: 7.1, actualPct: 0.026, varPct: -0.004, valuePerMt: 3510, totalValue: 24921,
    mc: 12.5, blacks: 4.0, brokens: 18.0, blackBroken: 22.0, fm: 2.5, s18: 30, s16: 70, s13: 95 },
  { code: 'R12', category: 'By-products', premium: -180, bomPct: 0.020, actualMt: 5.4, actualPct: 0.020, varPct: 0.000, valuePerMt: 3450, totalValue: 18630,
    mc: 12.6, blacks: 6.0, brokens: 20.0, blackBroken: 26.0, fm: 3.0, s18: 0, s16: 0, s13: 60 },
  { code: 'R-DF', category: 'By-products', premium: -200, bomPct: 0.015, actualMt: 4.6, actualPct: 0.017, varPct: 0.002, valuePerMt: 3430, totalValue: 15778,
    mc: 11.5, blacks: 5.0, brokens: 22.0, blackBroken: 27.0, fm: 4.5, s18: 0, s16: 20, s13: 75 },
  { code: 'R-HDW', category: 'By-products', premium: -250, bomPct: 0.010, actualMt: 3.1, actualPct: 0.012, varPct: 0.002, valuePerMt: 3380, totalValue: 10478,
    mc: 12.0, blacks: 8.0, brokens: 30.0, blackBroken: 38.0, fm: 6.0, s18: 0, s16: 0, s13: 50 },
  { code: 'R-DSR', category: 'Rejects', premium: -400, bomPct: 0.035, actualMt: 8.6, actualPct: 0.032, varPct: -0.003, valuePerMt: 1500, totalValue: 12900,
    mc: 12.9, blacks: 0.5, brokens: 0.9, blackBroken: 1.4, fm: 95, s18: 0, s16: 0, s13: 0 },
  { code: 'R-P16-CL', category: 'Graded', premium: 280, bomPct: 0.025, actualMt: 6.7, actualPct: 0.025, varPct: 0.000, valuePerMt: 3910, totalValue: 26197,
    mc: 12.8, blacks: 0.3, brokens: 1.1, blackBroken: 1.4, fm: 0.2, s18: 93.5, s16: 99.0, s13: 100 },
  { code: 'R13-GTR A', category: 'By-products', premium: -100, bomPct: 0.010, actualMt: 2.9, actualPct: 0.011, varPct: 0.001, valuePerMt: 3550, totalValue: 10295,
    mc: 12.4, blacks: 9.0, brokens: 35.0, blackBroken: 44.0, fm: 3.5, s18: 0, s16: 23, s13: 78 },
  { code: 'R13-GTR B', category: 'By-products', premium: -130, bomPct: 0.005, actualMt: 1.5, actualPct: 0.006, varPct: 0.001, valuePerMt: 3510, totalValue: 5265,
    mc: 12.5, blacks: 11.0, brokens: 40.0, blackBroken: 51.0, fm: 4.5, s18: 0, s16: 18, s13: 70 },
]

export interface LossSummary {
  totalLoss: number
  totalLossPct: number
  mcIn: number
  mcOut: number
  mcLoss: number
  mcLossPct: number
  unexpLoss: number
  unexpLossPct: number
  bomLoss: number
  variance: number
}

export const LOSS_SUMMARY: LossSummary = {
  totalLoss: 12.751,
  totalLossPct: 0.0452,
  mcIn: 13.04,
  mcOut: 12.55,
  mcLoss: 1.38,
  mcLossPct: 0.0049,
  unexpLoss: 11.371,
  unexpLossPct: 0.0403,
  bomLoss: 0.045,
  variance: 0.0002,
}

export interface ExtractedValueSummary {
  valueIn: number
  valueOut: number
  valueExtracted: number
  valueExtractedPerMt: number
  handling: number
  valueExtractedPerMtAfterCost: number
  actualExtracted: number
  bomExtracted: number
  pnlPerMt: number
  totalPnl: number
}

export const EXTRACTED_VALUE_SUMMARY: ExtractedValueSummary = {
  valueIn: 1020901,
  valueOut: 1054669,
  valueExtracted: 33768,
  valueExtractedPerMt: 119.66,
  handling: 12.5,
  valueExtractedPerMtAfterCost: 107.16,
  actualExtracted: 30222,
  bomExtracted: 27500,
  pnlPerMt: 9.65,
  totalPnl: 2722,
}

/* ---------- Standardization / Thông tin qui chuẩn (§5) -------------------- */

export interface StandardizationRow {
  batch: string
  stackId: string
  itemCode: string
  receiveNw: number
  deductPct: number
  deductNw: number
  itemAfter: string
  stackIdAfter: string
  reason: string
  stdBlacks: number
  stdBrokens: number
  stdFm: number
  blacksAfter: number
  brokensAfter: number
  fmAfter: number
  stdS18: number
  s18After: number
  stdS16: number
  s16After: number
  stdS13: number
  s13After: number
  stdExtra?: number
  calcExtra?: number
}

export const STANDARDIZATION_ROWS: StandardizationRow[] = [
  {
    batch: SAMPLE_PO_NO,
    stackId: 'BL-P-25097-46',
    itemCode: 'R13-GTR A',
    receiveNw: 4.242,
    deductPct: 1.0,
    deductNw: 4.242,
    itemAfter: 'R13-ST8',
    stackIdAfter: 'BL-P-25097-12',
    reason: 'BB cao hơn tiêu chuẩn — pha vào R13-ST8',
    stdBlacks: 0.5,
    stdBrokens: 1.5,
    stdFm: 0.3,
    blacksAfter: 0.42,
    brokensAfter: 1.18,
    fmAfter: 0.28,
    stdS18: 88.0,
    s18After: 88.4,
    stdS16: 95.0,
    s16After: 94.2,
    stdS13: 99.0,
    s13After: 99.0,
  },
  {
    batch: SAMPLE_PO_NO,
    stackId: 'BL-P-25097-44',
    itemCode: 'R13-GTR A',
    receiveNw: 11.501,
    deductPct: 0.0,
    deductNw: 0,
    itemAfter: '',
    stackIdAfter: '',
    reason: 'Không qui chuẩn — giữ nguyên',
    stdBlacks: 0.5,
    stdBrokens: 1.5,
    stdFm: 0.3,
    blacksAfter: 12,
    brokensAfter: 45.9,
    fmAfter: 4.89,
    stdS18: 88.0,
    s18After: 0,
    stdS16: 95.0,
    s16After: 23.69,
    stdS13: 99.0,
    s13After: 0,
  },
  {
    batch: SAMPLE_PO_NO,
    stackId: 'BL-P-25097-31',
    itemCode: 'R18-CL1',
    receiveNw: 28.1,
    deductPct: 0.03,
    deductNw: 0.843,
    itemAfter: 'R18-CL1',
    stackIdAfter: 'BL-P-25097-31',
    reason: 'MC cao hơn tiêu chuẩn 12.5% — qui chuẩn MC',
    stdBlacks: 0.5,
    stdBrokens: 1.5,
    stdFm: 0.3,
    blacksAfter: 0.2,
    brokensAfter: 0.9,
    fmAfter: 0.16,
    stdS18: 95.0,
    s18After: 98.5,
    stdS16: 99.0,
    s16After: 100,
    stdS13: 99.5,
    s13After: 100,
  },
]

/* ---------- Comparison (§6) ----------------------------------------------- */

export interface YieldComparisonRow {
  item: string
  category: string
  premium: number
  previous: number
  current: number
  variance: number
  varianceNorm: number
}

export const YIELD_COMPARISON_ROWS: YieldComparisonRow[] = [
  { item: 'R13-ST1', category: 'Graded', premium: 220, previous: 0.142, current: 0.153, variance: 0.011, varianceNorm: 0.078 },
  { item: 'R13-ST8', category: 'Graded', premium: 180, previous: 0.138, current: 0.144, variance: 0.006, varianceNorm: 0.043 },
  { item: 'R16-CL1', category: 'Graded', premium: 250, previous: 0.130, current: 0.135, variance: 0.005, varianceNorm: 0.038 },
  { item: 'R18-CL1', category: 'Graded', premium: 320, previous: 0.108, current: 0.104, variance: -0.004, varianceNorm: -0.038 },
  { item: 'R16-CSR', category: 'Graded', premium: 200, previous: 0.074, current: 0.072, variance: -0.002, varianceNorm: -0.027 },
  { item: 'R18-CSR', category: 'Graded', premium: 260, previous: 0.056, current: 0.055, variance: -0.001, varianceNorm: -0.018 },
  { item: 'R13-GTR', category: 'By-products', premium: -150, previous: 0.041, current: 0.043, variance: 0.002, varianceNorm: 0.049 },
  { item: 'R-P16-ST', category: 'By-products', premium: -120, previous: 0.028, current: 0.026, variance: -0.002, varianceNorm: -0.071 },
  { item: 'R12', category: 'By-products', premium: -180, previous: 0.022, current: 0.020, variance: -0.002, varianceNorm: -0.091 },
  { item: 'R-DF', category: 'By-products', premium: -200, previous: 0.014, current: 0.017, variance: 0.003, varianceNorm: 0.214 },
  { item: 'R-HDW', category: 'By-products', premium: -250, previous: 0.011, current: 0.012, variance: 0.001, varianceNorm: 0.091 },
  { item: 'R-DSR', category: 'Rejects', premium: -400, previous: 0.038, current: 0.032, variance: -0.006, varianceNorm: -0.158 },
  { item: 'R-P16-CL', category: 'Graded', premium: 280, previous: 0.022, current: 0.025, variance: 0.003, varianceNorm: 0.136 },
  { item: 'R13-GTR A', category: 'By-products', premium: -100, previous: 0.012, current: 0.011, variance: -0.001, varianceNorm: -0.083 },
  { item: 'R13-GTR B', category: 'By-products', premium: -130, previous: 0.004, current: 0.006, variance: 0.002, varianceNorm: 0.500 },
]

export interface FaqQualityCompareRow {
  po: string
  mc: number
  blacks: number
  brokens: number
  bb: number
  fm: number
  mouldy: number
  brown: number
  partlyBlack: number
  immature: number
  s18: number
  s16: number
  s13: number
}

export const FAQ_QUALITY_COMPARE_ROWS: FaqQualityCompareRow[] = [
  { po: SAMPLE_COMPARISON_PO_NO, mc: 13.10, blacks: 1.80, brokens: 2.85, bb: 4.65, fm: 1.42, mouldy: 0.55, brown: 0.12, partlyBlack: 0.18, immature: 0.32, s18: 40.5, s16: 78.0, s13: 91.5 },
  { po: SAMPLE_PO_NO, mc: 13.04, blacks: 1.95, brokens: 2.71, bb: 4.66, fm: 1.34, mouldy: 0.50, brown: 0.10, partlyBlack: 0.20, immature: 0.30, s18: 41.82, s16: 79.31, s13: 92.4 },
  { po: 'Variance', mc: -0.06, blacks: 0.15, brokens: -0.14, bb: 0.01, fm: -0.08, mouldy: -0.05, brown: -0.02, partlyBlack: 0.02, immature: -0.02, s18: 1.32, s16: 1.31, s13: 0.9 },
]

export interface NarrativeNoteRow {
  item: string
  mc: string
  bb: string
  fm: string
  s18: string
  s16: string
  s13: string
  note: string
  standardized: string
}

export const NARRATIVE_NOTE_ROWS: NarrativeNoteRow[] = [
  { item: 'R13-ST8', mc: 'Lower than std', bb: 'Lower than std', fm: 'Higher than std', s18: 'Met', s16: 'Met', s13: 'Met', note: 'FM cao hơn tiêu chuẩn — pha thêm by-products để cân bằng.', standardized: 'Yes' },
  { item: 'R16-CL1', mc: 'Met', bb: 'Slightly higher', fm: 'Met', s18: 'Met', s16: 'Met', s13: 'Met', note: 'BB slightly higher than std; no standardization needed.', standardized: 'No' },
  { item: 'R18-CL1', mc: 'Higher than std', bb: 'Met', fm: 'Met', s18: 'Met', s16: 'Met', s13: 'Met', note: 'MC > 12.5% — qui chuẩn MC về 12.5%.', standardized: 'Yes' },
  { item: 'R-P16-CL', mc: 'Met', bb: 'Met', fm: 'Met', s18: 'Met', s16: 'Met', s13: 'Met', note: 'Meets all standards.', standardized: 'No' },
]

export interface PriceInfo {
  liffe: number
  diff: number
  premiumVsG2: number
}

export const PRICE_INFO: PriceInfo = {
  liffe: 3450,
  diff: 180,
  premiumVsG2: 220,
}

export interface ProductionInfo {
  totalInput: number
  totalOutturn: number
  loss: number
  mcLoss: number
  unexplainedLoss: number
}

export const PRODUCTION_INFO: ProductionInfo = {
  totalInput: 282.163,
  totalOutturn: 0.9548,
  loss: 0.0452,
  mcLoss: 0.0049,
  unexplainedLoss: 0.0403,
}

/* ---------- BOM-BTA1 (§7) ------------------------------------------------- */

export interface BomHeader {
  bomCode: string
  inputQuality: string
  targetQuality: string
  year: number
  month: number
  lastRevised: string
  liffePrice: number
  g2Diffs: number
  purchaseMonth: string
}

export const BOM_HEADER: BomHeader = {
  bomCode: SAMPLE_BOM_CODE,
  inputQuality: 'RFAQ Robusta — Standard FAQ',
  targetQuality: 'Premium graded mix',
  year: 2026,
  month: 3,
  lastRevised: '2026-03-01',
  liffePrice: 3450,
  g2Diffs: 180,
  purchaseMonth: 'May 2026',
}

export interface BomKpis {
  totalInput: number
  totalValueIn: number
  totalOutturn: number
  totalValueOut: number
  variableProcessingCosts: number
  totalUpgradingCosts: number
  extractedValuePerMt: number
}

export const BOM_KPIS: BomKpis = {
  totalInput: 1.0,
  totalValueIn: 3630,
  totalOutturn: 0.955,
  totalValueOut: 3760,
  variableProcessingCosts: 38.5,
  totalUpgradingCosts: 51.0,
  extractedValuePerMt: 79.0,
}

export interface BomItemRow {
  item: string
  category: string
  premium: number
  value: number
  inputPct: number
  valueIn: number
  outputPct: number
  valueOut: number
  active: 'Y' | 'N'
  valueContribution: number
}

export const BOM_ITEM_ROWS: BomItemRow[] = [
  { item: 'RFAQ', category: 'FAQ', premium: 0, value: 3630, inputPct: 1.0, valueIn: 3630, outputPct: 0, valueOut: 0, active: 'Y', valueContribution: 3630 },
  { item: 'R13-ST1', category: 'Graded', premium: 220, value: 3850, inputPct: 0, valueIn: 0, outputPct: 0.14, valueOut: 539, active: 'Y', valueContribution: 539 },
  { item: 'R13-ST4', category: 'Graded', premium: 200, value: 3830, inputPct: 0, valueIn: 0, outputPct: 0.04, valueOut: 153.2, active: 'Y', valueContribution: 153.2 },
  { item: 'R13-ST8', category: 'Graded', premium: 180, value: 3810, inputPct: 0, valueIn: 0, outputPct: 0.135, valueOut: 514.35, active: 'Y', valueContribution: 514.35 },
  { item: 'R16-CL1', category: 'Graded', premium: 250, value: 3880, inputPct: 0, valueIn: 0, outputPct: 0.130, valueOut: 504.4, active: 'Y', valueContribution: 504.4 },
  { item: 'R18-CL1', category: 'Graded', premium: 320, value: 3950, inputPct: 0, valueIn: 0, outputPct: 0.110, valueOut: 434.5, active: 'Y', valueContribution: 434.5 },
  { item: 'R16-CSR', category: 'Graded', premium: 200, value: 3830, inputPct: 0, valueIn: 0, outputPct: 0.075, valueOut: 287.25, active: 'Y', valueContribution: 287.25 },
  { item: 'R18-CSR', category: 'Graded', premium: 260, value: 3890, inputPct: 0, valueIn: 0, outputPct: 0.060, valueOut: 233.4, active: 'Y', valueContribution: 233.4 },
  { item: 'R-P16-CL', category: 'Graded', premium: 280, value: 3910, inputPct: 0, valueIn: 0, outputPct: 0.025, valueOut: 97.75, active: 'Y', valueContribution: 97.75 },
  { item: 'R13-GTR', category: 'By-products', premium: -150, value: 3480, inputPct: 0, valueIn: 0, outputPct: 0.045, valueOut: 156.6, active: 'Y', valueContribution: 156.6 },
  { item: 'R-P16-ST', category: 'By-products', premium: -120, value: 3510, inputPct: 0, valueIn: 0, outputPct: 0.030, valueOut: 105.3, active: 'Y', valueContribution: 105.3 },
  { item: 'R12', category: 'By-products', premium: -180, value: 3450, inputPct: 0, valueIn: 0, outputPct: 0.020, valueOut: 69.0, active: 'Y', valueContribution: 69.0 },
  { item: 'R-DF', category: 'By-products', premium: -200, value: 3430, inputPct: 0, valueIn: 0, outputPct: 0.015, valueOut: 51.45, active: 'Y', valueContribution: 51.45 },
  { item: 'R-HDW', category: 'By-products', premium: -250, value: 3380, inputPct: 0, valueIn: 0, outputPct: 0.010, valueOut: 33.8, active: 'Y', valueContribution: 33.8 },
  { item: 'R-DSR', category: 'Rejects', premium: -400, value: 1500, inputPct: 0, valueIn: 0, outputPct: 0.035, valueOut: 52.5, active: 'Y', valueContribution: 52.5 },
  { item: 'Loss', category: 'Loss', premium: 0, value: 0, inputPct: 0, valueIn: 0, outputPct: 0.045, valueOut: 0, active: 'Y', valueContribution: 0 },
]

export interface VariableCost {
  label: string
  amount: number
}

export const VARIABLE_PROCESSING_COSTS: VariableCost[] = [
  { label: 'Power', amount: 18.0 },
  { label: 'Labor', amount: 20.5 },
  { label: 'Total', amount: 38.5 },
]

export const FX_RATE = 25430

export const HANDLING_COSTS: VariableCost[] = [
  { label: 'Nhập lên pallet', amount: 1.2 },
  { label: 'Vận chuyển đến hopper', amount: 2.4 },
  { label: 'Cắt xả hopper', amount: 1.8 },
  { label: 'Đóng bao', amount: 3.6 },
  { label: 'Vận chuyển vào kho', amount: 3.5 },
  { label: 'Total', amount: 12.5 },
]

export interface PowerConsumption {
  newMeter: number
  oldMeter: number
  kwhConsumed: number
  unitPrice: number
  powerCost: number
  powerCostPerTon: number
}

export const POWER_CONSUMPTION: PowerConsumption = {
  newMeter: 154820,
  oldMeter: 148500,
  kwhConsumed: 6320,
  unitPrice: 2850,
  powerCost: 18012000,
  powerCostPerTon: 63830,
}
